import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Row, Spin } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import {
  StyledModal,
  StyledModalHeader,
} from '../../../../common/form/FormElements.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useGetLastSyncResultQuery,
  useLdapSyncMutation,
} from '../../../../../redux/services/ldap';
import { DATETIME_FORMAT_DATETIME } from '../../../../../utils/helpers/CommonHelper.jsx';
import dayjs from 'dayjs';

const LDAPSynchronizationModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { successMessage } = useToastMessages();

  const [performLDAPSync, { isLoading, reset }] = useLdapSyncMutation();

  const {
    data: lastSyncResult,
    isFetching: isFetchingLastSync,
    error: lastSyncError,
    refetch: lastSyncRefetch,
  } = useGetLastSyncResultQuery(undefined, {
    pollingInterval: open ? 3000 : 0,
    refetchOnMountOrArgChange: true,
  });

  const isSynchronizationRunning =
    isLoading || Boolean(lastSyncResult?.in_progress);

  const handleLDAPSync = async () => {
    try {
      await performLDAPSync().unwrap();
      successMessage({
        message: 'LDAP synchronizácia úspešne dokončená',
        key: 'ldapSyncSuccess',
      });
    } catch (_) {
      // The shared axios interceptor displays the error toast; this modal only refreshes sync status.
    } finally {
      try {
        await lastSyncRefetch();
      } finally {
        reset();
      }
    }
  };

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      width={600}
      footer={null}
      destroyOnHidden
    >
      <StyledModalHeader>{t('LDAP synchronizácia')}</StyledModalHeader>

      <p>
        {t(
          'LDAP synchronizácia slúži na import údajov z fakultného AD do databázy portálu FRI. Operácia trvá cca 1-2 minúty.',
        )}
      </p>

      <p>{t('Táto činnosť je vykonávaná automaticky každý deň o')} 3:45</p>

      <Card
        title={t('Posledný výsledok synchronizácie')}
        style={{ marginTop: '20px' }}
        styles={{
          header: {
            backgroundColor: '#f5f8ff',
          },
        }}
      >
        {isSynchronizationRunning ? (
          <Spin
            size="large"
            spinning
            description={
              <span style={{ whiteSpace: 'nowrap' }}>
                {t('Prebieha LDAP synchronizácia, prosím čakajte.')}
              </span>
            }
          >
            <div style={{ minHeight: 72 }} />
          </Spin>
        ) : isFetchingLastSync && !lastSyncResult && !lastSyncError ? (
          <Spin size="small" />
        ) : lastSyncError ? (
          <p>{t('Žiadne výsledky synchronizácie nie sú dostupné')}</p>
        ) : (
          <>
            <p>
              <span className="bold">{t('Čas začiatku synchronizácie')}:</span>{' '}
              {dayjs(lastSyncResult?.start_time).format(
                DATETIME_FORMAT_DATETIME,
              )}
            </p>
            <p>
              <span className="bold">{t('Výsledok synchronizácie')}:</span>{' '}
              {lastSyncResult?.success ? (
                <>
                  <CheckCircleFilled style={{ color: 'green' }} />{' '}
                  <span style={{ color: 'green' }}>{t('Úspešná')}</span>
                </>
              ) : (
                <>
                  <CloseCircleFilled style={{ color: 'red' }} />{' '}
                  <span style={{ color: 'red' }}>{t('Neúspešná')}</span>
                </>
              )}
            </p>
            <p>
              <span className="bold">{t('Doba trvania')}:</span>{' '}
              {lastSyncResult?.execution_time || '-'}
            </p>
            {!lastSyncResult?.success && lastSyncResult?.error_message ? (
              <p>
                <span className="bold">{t('Chybová správa')}:</span>{' '}
                {lastSyncResult.error_message}
              </p>
            ) : null}
          </>
        )}
      </Card>

      <Row style={{ marginTop: '20px' }}>
        <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            disabled={isSynchronizationRunning}
            loading={isSynchronizationRunning}
            onClick={handleLDAPSync}
          >
            {t('Spustiť synchronizáciu')}
          </Button>
        </Col>
      </Row>
    </StyledModal>
  );
};

LDAPSynchronizationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default LDAPSynchronizationModal;
